import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { useCookies } from 'react-cookie';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getFlags } from '~/core/launch-darkly/flags';
import { LAUNCH_DARKLY_TRACKED_FLAGS_COOKIE_NAME } from '../../config';

/**
 * This configures which flags we are tracking due to data limitations both on
 * MixPanel / Segment events and on cookies.
 */
const TRACKED_FLAGS = ['kis511H22022BookingFlow'];

export const LDProxyContext = React.createContext({
  flags: getFlags(),
});

/**
 * This Wrapper for application uses a Javascript Proxy object on top of the Launch Darkly SDK in order
 * to track which flags are getting assigned to the user. This is necessary because the Launch Darkly
 * SDK does not provide a way to get a list of all flags that are being used in the application without
 * assigning the user to each flag for an experiment.
 */
export const LDProxyProvider = ({ children }: any) => {
  // eslint-disable-next-line no-restricted-syntax
  const ldFlags = useFlags();
  const [cookies, setCookies] = useCookies([LAUNCH_DARKLY_TRACKED_FLAGS_COOKIE_NAME]);
  const [newFlags, setNewFlags] = useState({});
  const currentTrackedFlags: Record<string, any> = useMemo(() => {
    return TRACKED_FLAGS.reduce((prev, f) => {
      if ((cookies[LAUNCH_DARKLY_TRACKED_FLAGS_COOKIE_NAME] ?? {}).hasOwnProperty(f)) {
        return { ...prev, [f]: cookies[LAUNCH_DARKLY_TRACKED_FLAGS_COOKIE_NAME][f] };
      }
      return prev;
    }, {});
  }, [cookies]);

  useEffect(() => {
    setCookies(
      LAUNCH_DARKLY_TRACKED_FLAGS_COOKIE_NAME,
      { ...currentTrackedFlags, ...newFlags },
      {
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'setYear' does not exist on type 'Date'.
        expires: new Date(new Date().setYear(2099)),
        path: '/',
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newFlags]);

  const getFunc = useCallback(
    (target: LDFlagSet, property: string) => {
      const val = target[property];

      if (
        val !== undefined &&
        TRACKED_FLAGS.includes(property) &&
        (!currentTrackedFlags.hasOwnProperty(property) || currentTrackedFlags[property] !== val)
      ) {
        setNewFlags((prev) => ({ ...prev, [property]: val }));
      }

      return val;
    },
    [currentTrackedFlags]
  );

  const handler = {
    get: getFunc,
  };

  const flags = new Proxy(ldFlags, handler);

  return <LDProxyContext.Provider value={{ flags }}>{children}</LDProxyContext.Provider>;
};
